import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  ComposedChart,
} from "recharts";
import Http from "../../Common/Http";
// import ReactDataTable from '../../Common/ReactDataTable.jsx';

import Table from "../Production/RequisitionProduction/List"

// Add CSS animation for loading spinner
const spinnerStyle = document.createElement("style");
spinnerStyle.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(spinnerStyle);

const SupplyChainAndProduction = () => {
  const navigate = useNavigate();
  const [rawMaterialsData, setRawMaterialsData] = useState([]);
  const [packingMaterialsData, setPackingMaterialsData] = useState([]);
  const [stockData, setStockData] = useState([]);
  const [filteredStockData, setFilteredStockData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState("monthly");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  // Handle product click to navigate to product ledger
  const handleProductClick = (product) => {
    navigate(
      `/product-ledger/:id?productId=${product.id}&productCode=${
        product.code
      }&productName=${encodeURIComponent(product.name)}`
    );
  };

  // Handle table sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // If clicking the same field, toggle direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If clicking a new field, set it as sort field with ascending direction
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Sort data function
  const sortData = (data, field, direction) => {
    if (!field) return data;

    return [...data].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];

      // Handle different data types
      if (field === "stock_qty") {
        aValue = parseFloat(aValue || 0);
        bValue = parseFloat(bValue || 0);
      } else if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (direction === "asc") {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };
  // Fetch data from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log("Fetching stock data from API...");

        // Fetch stock data
        // const stockResponse = await Http.get("/product-wise-stock");
        const stockResponse = await Http.get("/requisition-production");
        console.log("Stock API response:", stockResponse);

        if (stockResponse.data.status === "success") {
          console.log(stockResponse);
          setStockData(stockResponse.data);
        } else {
          console.log("API returned non-success status:", stockResponse.data);
          setStockData([]);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching supply chain data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle search form submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // Search is handled by the useEffect above
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <style>{`
        .hover-row:hover {
          background-color: #f8f9fa !important;
          transform: scale(1.01);
          transition: all 0.2s ease;
        }
        .hover-row {
          transition: all 0.2s ease;
        }
      `}</style>
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Supply Chain Dashboard</h5>
          <form
            id="searchBar"
            className="search-bar"
            onSubmit={handleSearchSubmit}
          >
            <div className="form-control-wrapper">
              <input
                type="search"
                className="form-control bd-0"
                placeholder="Search products by name, code, category, or unit..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
            <button id="searchBtn" className="btn btn-orange" type="submit">
              <i className="fa fa-search" />
            </button>
            {searchTerm && (
              <button
                type="button"
                className="btn btn-secondary ml-2"
                onClick={() => setSearchTerm("")}
                title="Clear search"
              >
                <i className="fa fa-times" />
              </button>
            )}
          </form>
        </div>

        <div className="am-pagebody" style={{ padding: "10px" }}>
          <div className="row row-sm mg-t-20">
            <div className="col-md-12">
              <div className="card pd-20 pd-sm-40">
                {/* Enhanced Stock Summary Cards */}
                  <>
                    <style>
                      {`
                      .dashboard-stats-container {
                        padding: 0.75rem 0;
                      }
                      
                      .stats-card {
                        background: linear-gradient(135deg, var(--card-bg-start), var(--card-bg-end));
                        border: none;
                        border-radius: 12px;
                        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
                        transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                        overflow: hidden;
                        position: relative;
                        backdrop-filter: blur(10px);
                        min-height: 100px;
                      }
                      
                      .stats-card::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        height: 3px;
                        background: linear-gradient(90deg, rgba(255,255,255,0.3), rgba(255,255,255,0.8), rgba(255,255,255,0.3));
                        animation: shimmer 2s infinite;
                      }
                      
                      @keyframes shimmer {
                        0% { transform: translateX(-100%); }
                        100% { transform: translateX(100%); }
                      }
                      
                      .stats-card:hover {
                        transform: translateY(-4px) scale(1.01);
                        box-shadow: 0 12px 25px rgba(0, 0, 0, 0.15);
                      }
                      
                      .stats-card.primary {
                        --card-bg-start: #667eea;
                        --card-bg-end: #764ba2;
                      }
                      
                      .stats-card.success {
                        --card-bg-start: #11998e;
                        --card-bg-end: #38ef7d;
                      }
                      
                      .stats-card.warning {
                        --card-bg-start: #f093fb;
                        --card-bg-end: #f5576c;
                      }
                      
                      .stats-card.danger {
                        --card-bg-start: #fc466b;
                        --card-bg-end: #3f5efb;
                      }
                      
                      .card-content {
                        padding: 1rem;
                        display: flex;
                        align-items: center;
                        position: relative;
                        z-index: 2;
                        height: 100%;
                      }
                      
                      .icon-wrapper {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 45px;
                        height: 45px;
                        background: rgba(255, 255, 255, 0.2);
                        border-radius: 50%;
                        backdrop-filter: blur(10px);
                        border: 2px solid rgba(255, 255, 255, 0.3);
                        transition: all 0.3s ease;
                        flex-shrink: 0;
                        margin-right: 1rem;
                      }
                      
                      .stats-card:hover .icon-wrapper {
                        transform: rotate(360deg) scale(1.05);
                        background: rgba(255, 255, 255, 0.3);
                      }
                      
                      .icon-wrapper i {
                        font-size: 1.2rem;
                        color: white;
                        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
                      }
                      
                      .text-content {
                        flex: 1;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        text-align: left;
                      }
                      
                      .stats-number {
                        font-size: 1.6rem;
                        font-weight: 700;
                        color: white;
                        margin: 0 0 0.25rem 0;
                        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
                        line-height: 1;
                        animation: countUp 0.8s ease-out;
                      }
                      
                      @keyframes countUp {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                      }
                      
                      .stats-label {
                        font-size: 0.75rem;
                        font-weight: 500;
                        color: rgba(255, 255, 255, 0.9);
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        margin: 0;
                        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
                      }
                      
                      .stats-trend {
                        position: absolute;
                        top: 0.75rem;
                        right: 0.75rem;
                        background: rgba(255, 255, 255, 0.2);
                        border-radius: 12px;
                        padding: 0.2rem 0.5rem;
                        font-size: 0.65rem;
                        font-weight: 600;
                        color: white;
                        backdrop-filter: blur(5px);
                      }
                      
                      .floating-elements {
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        overflow: hidden;
                        pointer-events: none;
                      }
                      
                      .floating-elements::before,
                      .floating-elements::after {
                        content: '';
                        position: absolute;
                        width: 50px;
                        height: 50px;
                        border-radius: 50%;
                        background: rgba(255, 255, 255, 0.06);
                        animation: float 6s ease-in-out infinite;
                      }
                      
                      .floating-elements::before {
                        top: -25px;
                        right: -25px;
                        animation-delay: 0s;
                      }
                      
                      .floating-elements::after {
                        bottom: -25px;
                        left: -25px;
                        animation-delay: 3s;
                      }
                      
                      @keyframes float {
                        0%, 100% { transform: translateY(0px) rotate(0deg); }
                        50% { transform: translateY(-8px) rotate(180deg); }
                      }
                      
                      /* Laptop/Desktop optimized sizes */
                      @media (min-width: 992px) and (max-width: 1400px) {
                        .stats-card {
                          min-height: 95px;
                        }
                        .card-content {
                          padding: 0.9rem;
                        }
                        .icon-wrapper {
                          width: 42px;
                          height: 42px;
                          margin-right: 0.9rem;
                        }
                        .icon-wrapper i {
                          font-size: 1.1rem;
                        }
                        .stats-number {
                          font-size: 1.5rem;
                        }
                        .stats-label {
                          font-size: 0.7rem;
                        }
                        .stats-trend {
                          padding: 0.15rem 0.4rem;
                          font-size: 0.6rem;
                        }
                      }
                      
                      @media (max-width: 991px) {
                        .stats-card {
                          margin-bottom: 0.75rem;
                          min-height: 90px;
                        }
                        .card-content {
                          padding: 0.8rem;
                        }
                        .stats-number {
                          font-size: 1.4rem;
                        }
                        .icon-wrapper {
                          width: 40px;
                          height: 40px;
                          margin-right: 0.8rem;
                        }
                        .icon-wrapper i {
                          font-size: 1rem;
                        }
                        .stats-label {
                          font-size: 0.7rem;
                        }
                      }
                      
                      @media (max-width: 576px) {
                        .card-content {
                          flex-direction: column;
                          text-align: center;
                          padding: 0.75rem;
                        }
                        .icon-wrapper {
                          margin-right: 0;
                          margin-bottom: 0.5rem;
                          width: 35px;
                          height: 35px;
                        }
                        .icon-wrapper i {
                          font-size: 0.9rem;
                        }
                        .text-content {
                          text-align: center;
                        }
                        .stats-number {
                          font-size: 1.2rem;
                        }
                        .stats-label {
                          font-size: 0.65rem;
                        }
                      }
                    `}
                    </style>

                    <div className="dashboard-stats-container m-0">
                      <div className="row">
                        <div className="col-lg-4 col-md-6 col-sm-6 m-0">
                          <div className="card stats-card primary text-white m-0">
                            <div className="floating-elements"></div>
                            <div className="stats-trend">
                              <i className="fa fa-arrow-up"></i> Active
                            </div>
                            <div className="card-content">
                              <div className="icon-wrapper">
                                <i className="fa fa-cubes"></i>
                              </div>
                              <div className="text-content">
                                <div className="stats-number">
                                  {stockData.list.length - stockData.approved}
                                </div>
                                <div className="stats-label">
                                  Total Pending Approvals
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-4 col-md-6 col-sm-6 m-0">
                          <div className="card stats-card success text-white m-0">
                            <div className="floating-elements"></div>
                            <div className="stats-trend">
                              <i className="fa fa-check"></i> Good
                            </div>
                            <div className="card-content">
                              <div className="icon-wrapper">
                                <i className="fa fa-check-circle"></i>
                              </div>
                              <div className="text-content">
                                <div className="stats-number">
                                  {stockData.approved}
                                </div>
                                <div className="stats-label">Total Approved</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-4 col-md-6 col-sm-6 m-0">
                          <div className="card stats-card warning text-white m-0">
                            <div className="floating-elements"></div>
                            <div className="stats-trend">
                              <i className="fa fa-exclamation"></i> Alert
                            </div>
                            <div className="card-content">
                              <div className="icon-wrapper">
                                <i className="fa fa-exclamation-triangle"></i>
                              </div>
                              <div className="text-content">
                                <div className="stats-number">
                                  {stockData.today_new}
                                </div>
                                <div className="stats-label">Today New Requisitions</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
              </div>
            </div>
          </div>
          {/* Beautiful Card Container for Stock Management Table */}
         <Table allowMMargin={false} />
        </div>

        <div className="am-footer">
          <span>
            Copyright ©. All Rights Reserved.{" "}
            <a href="http://limerickbd.com/">Limerick Resources Limited</a>
          </span>
        </div>
      </div>
    </div>
  );
};

export default SupplyChainAndProduction;
